import { useState } from "react";
import BoxControls from "../../Common/Box/BoxControls";
import SectorChooser from "../../Common/Box/Choosers/SectorChooser";
import Figure from "../../Common/Box/Figure";
import initialData from "../../../data/initialData.json";
import useAreaQuery from "../../../hooks/useAreaQuery";
import { getSector, getTooltip } from "../../../utilities";

const number = 4;

const getTitle = (sectorId) =>
  `Method Mix within the ${
    sectorId === 0 ? "Public and Private" : getSector(sectorId)
  } Sector`;

const getSeriesColor = (name, theme) => {
  switch (name) {
    case "Abortion":
      return theme.palette.areas.abortion.methods.abortion;

    case "PAC":
      return theme.palette.areas.abortion.methods.pac;

    case "Sterilization":
      return theme.palette.areas.contraception.methods.sterilization;

    case "Implant":
      return theme.palette.areas.contraception.methods.implant;

    case "IUD":
      return theme.palette.areas.contraception.methods.iud;

    case "Injectable":
      return theme.palette.areas.contraception.methods.injectable;

    case "Pill":
      return theme.palette.areas.contraception.methods.pill;

    case "Condom":
      return theme.palette.areas.contraception.methods.condom;

    case "Other":
      return theme.palette.areas.contraception.methods.other;

    default:
      return "black";
  }
};

const options = (data, props) => (theme) => {
  const absolute = props.comparisonMode !== "relative";

  const series = data.map((series) => ({
    ...series,
    color: getSeriesColor(series.name, theme),
  }));

  return {
    chart: {
      type: "column",
      width: 470,
      height: 300,
      backgroundColor: null,
    },
    xAxis: {
      categories: ["Low", "Lower-Middle", "Upper-Middle"],
      title: undefined,
      labels: {
        autoRotation: undefined,
        useHTML: true,
        style: {
          color: theme.figure.axis.primary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    yAxis: {
      title: undefined,
      min: 0,
      reversedStacks: false,
      labels: {
        format: absolute ? undefined : "{value}%",
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    plotOptions: {
      column: {
        stacking: absolute ? "normal" : "percent",
        pointWidth: 30,
        borderWidth: 0,
      },
    },
    legend: {
      align: "right",
      verticalAlign: "middle",
      layout: "vertical",
      reversed: true,
      itemStyle: {
        fontSize: theme.figure.legend.fontSize,
      },
    },
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: function () {
        return getTooltip("Users", this.y, this.percentage);
      },
      style: {
        fontSize: "0.875rem",
        color: theme.palette.tooltip.primary,
      },
    },
    exporting: {
      chartOptions: {
        title: undefined,
        subtitle: undefined,
      },
      enabled: false,
    },
    series,
  };
};

const Component = () => {
  const [selectedSectorId, setSelectedSectorId] = useState(0);
  const [comparisonMode, setComparisonMode] = useState("relative");

  const { areaData: data } = useAreaQuery(
    `/fp/figures/4/${selectedSectorId}`,
    initialData["fp_f4"]
  );

  const handleSectorChange = (methodId) => {
    setSelectedSectorId(methodId === 4 ? 0 : methodId);
  };

  const handleComparisonModeToggle = () => {
    setComparisonMode((comparisonMode) =>
      comparisonMode === "absolute" ? "relative" : "absolute"
    );
  };

  return (
    <Figure
      id="FP_f4"
      number={number.toString()}
      fileStem={`fp_f${number}`}
      title={getTitle(selectedSectorId)}
      subtitle="By Income Group, 2019"
      selection={getSector(selectedSectorId)}
      controls={
        <BoxControls
          comparisionMode={comparisonMode}
          onToggleComparisonMode={handleComparisonModeToggle}
        >
          <SectorChooser
            value={selectedSectorId}
            onChange={handleSectorChange}
          />
        </BoxControls>
      }
      chartOptions={options(data, { comparisonMode })}
    />
  );
};

export default Component;
